import { faCheck, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { clone, map } from 'lodash';
import { useReducer, useRef, useState } from 'react';

import { Button, type ButtonProps } from '../button/button';

interface Option {
  label: string;
  value: string;
  isActive?: boolean;
}

export interface DropdownMultiSelectProps
  extends Pick<ButtonProps, 'content' | 'icon' | 'muiIcon' | 'size'> {
  title: string;
  options: Option[];
  onApply: (options: Option[]) => void;
  resetSelections?: boolean;
  showSelectedText?: boolean;
}

function filterReducer(
  allFilters: Option[],
  action:
    | { type: 'add_option' | 'remove_option'; idx: number }
    | { type: 'reset'; initialState: Option[] }
    | { type: 'select_all' }
    | { type: 'clear_all' }
    | { type: 'only'; idx: number },
): Option[] {
  switch (action.type) {
    case 'add_option':
      allFilters[action.idx].isActive = true;
      break;
    case 'remove_option':
      allFilters[action.idx].isActive = false;
      break;
    case 'reset':
      allFilters = action.initialState;
      break;
    case 'select_all':
      allFilters.forEach((filter) => {
        filter.isActive = true;
      });
      break;
    case 'clear_all':
      allFilters.forEach((filter) => {
        filter.isActive = false;
      });
      break;
    case 'only':
      allFilters.forEach((filter, idx) => {
        filter.isActive = idx === action.idx;
      });
      break;

    default:
      throw Error('Unknown action.');
  }

  // cloning to trigger state change
  return clone(allFilters);
}

export function DropdownMultiSelect({
  content,
  icon,
  onApply,
  options,
  resetSelections,
  title,
  size,
  showSelectedText,
}: DropdownMultiSelectProps) {
  const [shown, setShown] = useState<boolean>(false);
  const [allOptions, dispatchOptionAction] = useReducer(filterReducer, options);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const getSelectedText = () => {
    if (!showSelectedText) return title;

    const selectedOptions = allOptions.filter((option) => option.isActive);

    if (selectedOptions.length === 0) return `${title} (None)`;
    if (selectedOptions.length === allOptions.length) return title;

    if (selectedOptions.length <= 2) {
      return selectedOptions.map((option) => option.label).join(' or ');
    }
    const first = selectedOptions[0].label;
    const second = selectedOptions[1].label;
    const remaining = selectedOptions.length - 2;
    return `${first}, ${second} + ${remaining}`;
  };

  const handleToggle = () => {
    setShown(!shown);
  };

  const handleClose = () => {
    setShown(false);
  };

  return (
    <Box display="inline-block">
      <Button
        ref={anchorRef}
        onClick={handleToggle}
        content={getSelectedText()}
        icon={icon}
        size={size}
      />
      <Menu
        anchorEl={anchorRef.current}
        open={shown}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 250 },
        }}
      >
        {/* Select All / Clear All options */}
        <MenuItem
          onClick={() => {
            dispatchOptionAction({ type: 'select_all' });
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Select All
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            dispatchOptionAction({ type: 'clear_all' });
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Clear All
          </Typography>
        </MenuItem>
        <Divider />

        {/* Individual options with Only button */}
        {map(allOptions, (option, idx) => (
          <MenuItem
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 8px',
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              onClick={() => {
                dispatchOptionAction({
                  type: option.isActive ? 'remove_option' : 'add_option',
                  idx,
                });
              }}
              sx={{
                flex: 1,
                cursor: 'pointer',
                padding: '4px 0',
              }}
            >
              <Box sx={{ minWidth: '25px' }}>
                {option.isActive && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    style={{ marginLeft: '4px' }}
                    color="primary"
                  />
                )}
              </Box>
              <Typography variant="body2">{option.label}</Typography>
            </Box>
            <Button
              content="Only"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                dispatchOptionAction({ type: 'only', idx });
              }}
              sx={{
                minWidth: 'auto',
                padding: '2px 8px',
                fontSize: '0.75rem',
              }}
            />
          </MenuItem>
        ))}
        <Divider />
        <Box p={1}>
          <Stack direction="row" spacing={1} justifyContent="center">
            {resetSelections && (
              <Button
                content="Reset"
                variant="ghost"
                icon={faUndo}
                size="sm"
                onClick={() => {
                  dispatchOptionAction({
                    type: 'reset',
                    initialState: map(options, (o) => ({
                      ...o,
                      isActive: true,
                    })),
                  });
                }}
              />
            )}
            <Button
              content={content}
              icon={icon}
              size="sm"
              onClick={() => {
                onApply(allOptions);
                handleClose();
              }}
            />
          </Stack>
        </Box>
      </Menu>
    </Box>
  );
}
